import React from 'react';

/**
 * ProductionProgressBar — Light Orokin themed fill bar for creator posts.
 * Renders nothing when disabled or percent is null.
 *
 * Props:
 *  - data: { enabled: bool, label?: string, percent: number }
 *  - compact?: bool (smaller variant for FeedCard)
 */
export function ProductionProgressBar({ data, compact = false }) {
  if (!data || !data.enabled) return null;
  const percent = Math.max(0, Math.min(100, parseInt(data.percent ?? 0, 10) || 0));
  const label = (data.label || '').trim() || 'Concept-to-Shelf progress';
  const stages = [
    { p: 0, name: 'Concept' },
    { p: 25, name: 'Prototype' },
    { p: 50, name: 'Tooling' },
    { p: 75, name: 'Manufacturing' },
    { p: 100, name: 'On Shelf' },
  ];
  // Pick current stage (highest stage where percent >= stage.p)
  let currentStage = stages[0];
  for (const s of stages) if (percent >= s.p) currentStage = s;

  return (
    <div
      className={`feed-accent-soft mt-3 rounded-2xl border border-[hsl(var(--app-border))] ${compact ? 'p-2.5' : 'p-3'}`}
      data-testid="production-progress-bar"
      data-percent={percent}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className={`font-display ${compact ? 'text-xs' : 'text-sm'} font-medium text-[hsl(var(--foreground))] truncate`}>
          {label}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--gold-dim))]">
          {currentStage.name} · {percent}%
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--app-card-2))] ring-1 ring-[hsl(var(--gold)/0.25)]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[hsl(var(--gold-dim))] to-[hsl(var(--gold))]"
          style={{ width: `${percent}%`, transition: 'width 250ms ease-out' }}
          data-testid="production-progress-fill"
        />
      </div>
      {!compact && (
        <div className="mt-1.5 flex justify-between font-mono text-[9px] uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))]">
          {stages.map((s) => (
            <span key={s.name} className={percent >= s.p ? 'text-[hsl(var(--gold-dim))]' : ''}>
              {s.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProductionProgressBar;
